import React, { useState, useEffect } from 'react';
import { Filter, ArrowUpDown, Network, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import StatusCard from './StatusCard';
import TransitionsModal from './TransitionsModal';
import TransitionGraph from './TransitionGraph';
import { Checkbox, Autocomplete } from '@anyemp/ui-kit';
import SearchInput from '../UI/SearchInput';
import { Loader } from '../UI/LoadingSpinner';
import { statuses as statusService, stages as stageService } from '../../services/apiService';
import { getStatusFilterTypeOptions, getStatusTypes, getTalentTypeLabel } from '../../utils/talentTypeUtils';
import SavedFiltersDropdown from '../SavedFilters/SavedFiltersDropdown';
import { PAGE_KEYS } from '../../constants/savedFilterPageKeys';

const SAVED_FILTER_KEYS = ['search', 'talent_type', 'stage_ids', 'active_only', 'public_only', 'grouped'];
// Спецзначение в мультиселекте стадий — "статус не привязан ни к одной
// стадии". Бэкенд понимает его в списке stage_ids (см. buildStatusStageFilterCondition).
const UNLINKED_STAGE_FILTER = 'unlinked';

const StatusList = () => {
  const [statusesData, setStatusesData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [showTransitionsModal, setShowTransitionsModal] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  
  // Фильтры
  const [filters, setFilters] = useState({
    search: '',
    talent_type: '',
    stage_ids: [],
    active_only: true,
    public_only: false,
    grouped: false
  });
  
  // Пагинация
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 21,
    total: 0,
    totalPages: 0
  });

  const [showFilters, setShowFilters] = useState(false);
  const [statusesLoading, setStatusesLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);

  // Плоский список стадий — только для опций фильтра. Какие стадии привязаны
  // к какому статусу теперь приходит прямо в ответе GET /statuses (поле
  // status.stages, см. STATUS_STAGES_INCLUDE на бэкенде) — отдельный обход
  // всех стадий на клиенте для построения этой связи больше не нужен.
  const [stagesList, setStagesList] = useState([]);
  const [stagesListLoading, setStagesListLoading] = useState(true);

  const loadStagesList = async () => {
    try {
      setStagesListLoading(true);
      const stagesRes = await stageService.getStages();
      const fetchedStages = Array.isArray(stagesRes?.data) ? stagesRes.data : [];
      setStagesList([...fetchedStages].sort((a, b) => (a.stage_order || 0) - (b.stage_order || 0)));
    } catch (err) {
      console.error('Error loading stages list:', err);
    } finally {
      setStagesListLoading(false);
    }
  };

  useEffect(() => {
    loadStagesList();
  }, []);

  const loadStatuses = async () => {
    try {
      setStatusesLoading(true);
      setError(null);
      
      // stage_ids уходит на сервер отдельным полем join'ом через запятую —
      // это не тот же параметр, что одиночный GET /statuses?stage_id=X
      // (короткий путь "статусы одной стадии" для контрола Status на карточке
      // таланта, не трогаем). Здесь — обычный фильтр списка, применяется на
      // бэкенде вместе с search/talent_type/active_only/public_only.
      // grouped is not sent: the server's grouped mode ignores search /
      // talent_type / active_only / public_only and returns statuses without
      // talent_types. The grouped view loads the full filtered list instead
      // and groups it below.
      const { stage_ids, grouped, ...serverFilters } = filters;
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...serverFilters
      };
      if (grouped) {
        params.all = true;
      }
      if (Array.isArray(stage_ids) && stage_ids.length > 0) {
        params.stage_ids = stage_ids.join(',');
      }

      const response = await statusService.getAllStatuses(params);
      console.log('StatusList: API Response:', response);
      
      // Обрабатываем ответ независимо от структуры
      let statusesList = [];
      
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          statusesList = response.data;
        } else if (response.data.statuses && Array.isArray(response.data.statuses)) {
          statusesList = response.data.statuses;
        } else if (typeof response.data === 'object') {
          // Если это объект с группировкой
          statusesList = Object.values(response.data).flat();
        }
      }
      
      if (filters.grouped) {
        // Группируем статусы по типу. Статус таланта попадает в группу каждого
        // типа, к стадиям которого привязан; с бэкенда такой статус приходит в
        // нескольких группах, поэтому сначала убираем повторы
        const uniqueStatuses = [...new Map(statusesList.map(status => [status.id, status])).values()];
        const groupedData = uniqueStatuses.reduce((acc, status) => {
          // With a type filter on, a status linked to stages of several types
          // must not bring the other types' groups along
          const types = filters.talent_type ? [filters.talent_type] : getStatusTypes(status);
          for (const type of (types.length > 0 ? types : ['unknown'])) {
            if (!acc[type]) {
              acc[type] = [];
            }
            acc[type].push(status);
          }
          return acc;
        }, {});
        
        // Преобразуем в массив для рендеринга
        const groupedArray = Object.entries(groupedData).map(([type, statusList]) => ({
          type,
          statuses: statusList
        }));
        
        setStatusesData(groupedArray);
      } else {
        setStatusesData(statusesList);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || statusesList.length,
          totalPages: response.pagination?.totalPages || Math.ceil(statusesList.length / pagination.limit)
        }));
      }
    } catch (err) {
      console.error('Error loading statuses:', err);
      setError(err.message || 'Error loading statuses');
    } finally {
      setStatusesLoading(false);
    }
  };

  useEffect(() => {
    loadStatuses();
  }, [pagination.page, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };


  const handleUpdate = async (id, updateData) => {
    try {
      await statusService.updateStatus(id, updateData);
      setEditingStatusId(null); // Закрываем режим редактирования
      loadStatuses(); // Перезагружаем список
    } catch (error) {
      throw error; // Пробрасываем ошибку в StatusCard
    }
  };

  const handleDelete = async (id) => {
    try {
      await statusService.deleteStatus(id);
      loadStatuses(); // Перезагружаем список
    } catch (error) {
      throw error; // Пробрасываем ошибку в StatusCard
    }
  };

  const handleEditStatus = (statusId) => {
    setEditingStatusId(statusId);
  };

  const handleCancelEdit = () => {
    setEditingStatusId(null);
  };

  const handleViewTransitions = (status) => {
    setSelectedStatus(status);
    setShowTransitionsModal(true);
  };

  const handleCloseTransitionsModal = () => {
    setShowTransitionsModal(false);
    setSelectedStatus(null);
  };

  const handleTransitionCreated = (transitionData) => {
    toast.success('Transition created');
    // Можно добавить логику обновления данных
  };

  const handleTransitionUpdated = (transitionId, updateData) => {
    toast.success('Transition updated');
    // Можно добавить логику обновления данных
  };

  const handleTransitionDeleted = (transition) => {
    toast.success('Transition deleted');
    // Можно добавить логику обновления данных
  };

  // Force status sync handler
  const handleForceStatusSync = async () => {
    try {
      setSyncLoading(true);

      const response = await statusService.forceSyncStatuses();

      if (response.success) {
        toast.success('Statuses synchronized successfully from Libraries Service', {
          duration: 3000,
          position: 'bottom-right',
        });

        // Reload statuses after sync
        await loadStatuses();
      }
    } catch (err) {
      console.error('Error syncing statuses:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to sync statuses';
      toast.error(errorMessage, {
        duration: 4000,
        position: 'bottom-right',
      });
    } finally {
      setSyncLoading(false);
    }
  };

  // Стадийный фильтр теперь применяется на бэкенде (stage_ids в params —
  // см. loadStatuses), поэтому statusesData уже отфильтрован по стадии.
  // Считаем видимое количество прямо из того, что пришло с сервера.
  const visibleStatusesCount = filters.grouped
    ? statusesData.reduce((sum, group) => sum + (group.statuses || []).length, 0)
    : statusesData.length;

  const renderStatuses = () => {
    console.log('StatusList: Rendering statusesData:', statusesData);
    console.log('StatusList: filters.grouped:', filters.grouped);

    if (filters.grouped && statusesData.length > 0 && statusesData[0].type) {
      // Группированный режим
      return statusesData.map(group => {
        const groupStatuses = group.statuses || [];
        if (groupStatuses.length === 0) return null;
        return (
        <div key={group.type} className="mb-8">
          <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 capitalize">
            {getTalentTypeLabel(group.type)}
            <span className="ml-2 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">({groupStatuses.length})</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {groupStatuses.map(status => (
              <StatusCard
                key={status.id}
                status={status}
                stages={status.stages || []}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onViewTransitions={handleViewTransitions}
                isEditing={editingStatusId === status.id}
                onEdit={handleEditStatus}
                onCancelEdit={handleCancelEdit}
              />
            ))}
          </div>
        </div>
        );
      });
    }

    // Обычный режим
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {statusesData.map(status => (
          <StatusCard
            key={status.id}
            status={status}
            stages={status.stages || []}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onViewTransitions={handleViewTransitions}
            isEditing={editingStatusId === status.id}
            onEdit={handleEditStatus}
            onCancelEdit={handleCancelEdit}
          />
        ))}
      </div>
    );
  };


  return (
    <div className="space-y-6">
      {/* Заголовок и кнопки управления */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">Status Management</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleForceStatusSync}
            disabled={syncLoading}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-colors text-sm sm:text-base whitespace-nowrap ${
              syncLoading
                ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed text-gray-500 dark:text-gray-400'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
            title="Force synchronization of statuses from Libraries Service"
          >
            <RefreshCw className={`w-4 h-4 flex-shrink-0 ${syncLoading ? 'animate-spin' : ''}`} />
            {syncLoading ? 'Syncing...' : 'Sync Statuses'}
          </button>
          <button
            onClick={() => setShowGraph(!showGraph)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-md transition-colors text-sm sm:text-base whitespace-nowrap ${
              showGraph
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <Network className="w-4 h-4 flex-shrink-0" />
            {showGraph ? 'Hide Graph' : 'Show Graph'}
          </button>
          <button
            onClick={() => window.location.href = '/statuses/order-management'}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm sm:text-base whitespace-nowrap"
          >
            <ArrowUpDown className="w-4 h-4 flex-shrink-0" />
            Order Management
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-light-border-primary dark:border-dark-border-primary p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">Filters</h3>
          <div className="flex flex-wrap items-center gap-3">
            <SavedFiltersDropdown
              pageKey={PAGE_KEYS.STATUSES}
              currentFilters={filters}
              applyKeys={SAVED_FILTER_KEYS}
              onApply={(saved) => {
                setFilters(prev => ({ ...prev, ...saved }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {/* Поиск */}
            <div>
              <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">
                Search
              </label>
              <SearchInput
                value={filters.search}
                onChange={(value) => handleFilterChange('search', value)}
                placeholder="Search by name..."
                debounceTime={300}
                showIcon={true}
              />
            </div>

            {/* Тип таланта */}
            <Autocomplete
              clearable
              fullWidth
              label="Talent Type"
              options={getStatusFilterTypeOptions()}
              value={filters.talent_type}
              onChange={(value) => handleFilterChange('talent_type', value || '')}
              placeholder="Select talent type..."
            />

            {/* Стадия — множественный выбор, фильтрует на бэкенде
                (params.stage_ids, см. loadStatuses) */}
            <Autocomplete
              multiple
              clearable
              fullWidth
              label="Stage"
              options={[
                { value: UNLINKED_STAGE_FILTER, label: '⚠ Not linked to any stage' },
                ...stagesList.map((stage) => ({ value: String(stage.id), label: stage.name })),
              ]}
              value={filters.stage_ids}
              onChange={(value) => handleFilterChange('stage_ids', value || [])}
              placeholder={stagesListLoading ? 'Loading stages...' : 'Select stages...'}
              disabled={stagesListLoading}
            />

            {/* Checkboxes */}
            <div className="col-span-1 md:col-span-2 lg:col-span-4">
              <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-3">
                Display Options
              </label>
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                <Checkbox
                  label="Active Only"
                  color="primary"
                  size="small"
                  checked={!!filters.active_only}
                  onChange={(checked) => handleFilterChange('active_only', checked)}
                />
                <Checkbox
                  label="Public Only"
                  color="primary"
                  size="small"
                  checked={!!filters.public_only}
                  onChange={(checked) => handleFilterChange('public_only', checked)}
                />
                <Checkbox
                  label="Group by Types"
                  color="primary"
                  size="small"
                  checked={!!filters.grouped}
                  onChange={(checked) => handleFilterChange('grouped', checked)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Список статусов */}
      {statusesLoading ? (
        <Loader
          variant="component"
          text="Loading statuses..."
          size="md"
        />
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 dark:bg-red-900/30 dark:border-red-800">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Loading Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={loadStatuses}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-300 dark:hover:bg-red-900/70"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : visibleStatusesCount === 0 ? (
        <div className="text-center py-12 bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-light-border-primary dark:border-dark-border-primary">
          <div className="text-light-text-tertiary dark:text-dark-text-tertiary mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-2">No statuses found</h3>
          <p className="text-light-text-tertiary dark:text-dark-text-tertiary mb-4">
            Try adjusting the filters
          </p>
        </div>
      ) : (
        <>
          {renderStatuses()}
          
          {/* Пагинация */}
          {!filters.grouped && pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-light-border-primary bg-light-bg-primary px-4 py-3 dark:border-dark-border-primary dark:bg-dark-bg-secondary">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-light-border-primary dark:border-dark-border-primary text-sm font-medium rounded-md text-light-text-secondary dark:text-dark-text-secondary bg-light-bg-primary dark:bg-dark-bg-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-light-border-primary dark:border-dark-border-primary text-sm font-medium rounded-md text-light-text-secondary dark:text-dark-text-secondary bg-light-bg-primary dark:bg-dark-bg-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                    Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> - <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-light-border-primary dark:border-dark-border-primary bg-light-bg-primary dark:bg-dark-bg-secondary text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === pagination.page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900/30 dark:border-blue-400 dark:text-blue-400'
                              : 'bg-light-bg-primary dark:bg-dark-bg-secondary border-light-border-primary dark:border-dark-border-primary text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-light-border-primary dark:border-dark-border-primary bg-light-bg-primary dark:bg-dark-bg-secondary text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Graph View */}
      {showGraph && (
        <div className="mt-6">
          <TransitionGraph 
            talentType={filters.talent_type || null}
            onStatusClick={(status) => {
              setSelectedStatus(status);
              setShowTransitionsModal(true);
            }}
          />
        </div>
      )}

      {/* Transitions Modal */}
      <TransitionsModal
        isOpen={showTransitionsModal}
        onClose={handleCloseTransitionsModal}
        status={selectedStatus}
        onTransitionCreated={handleTransitionCreated}
        onTransitionUpdated={handleTransitionUpdated}
        onTransitionDeleted={handleTransitionDeleted}
      />
    </div>
  );
};

export default StatusList;
